import React from 'react';
import { motion } from 'framer-motion';
import { FiLink, FiShield, FiAlertTriangle, FiCheckCircle, FiActivity, FiTrendingUp, FiSettings, FiZap } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MotionDiv from '../components/MotionDiv';

const APISecurity = () => {
  const currentStats = [
    { label: "APIs Scanned", value: "42", description: "API endpoints analyzed", icon: <FiLink /> },
    { label: "Security Tests", value: "156", description: "security tests performed", icon: <FiActivity /> },
    { label: "Vulnerabilities Found", value: "18", description: "API security issues", icon: <FiAlertTriangle /> },
    { label: "Last Scan", value: "1", description: "day ago", icon: <FiTrendingUp /> }
  ];

  const coreFeatures = [
    {
      title: "API Discovery & Mapping",
      icon: <FiLink />,
      features: [
        "Automatic Discovery: Automated discovery of API endpoints and services",
        "API Documentation: Generation of comprehensive API documentation",
        "Endpoint Mapping: Complete mapping of API attack surface",
        "Version Detection: Identification of API versions and schemas"
      ]
    },
    {
      title: "Security Testing",
      icon: <FiZap />,
      features: [
        "Authentication Testing: OAuth, JWT, API key validation testing",
        "Authorization Testing: Access control and permission validation",
        "Input Validation: Parameter tampering and injection testing",
        "Rate Limiting: API rate limiting and throttling assessment"
      ]
    },
    {
      title: "Vulnerability Assessment",
      icon: <FiShield />,
      features: [
        "OWASP API Top 10: Testing against OWASP API security risks",
        "Business Logic Flaws: API-specific business logic vulnerability detection",
        "Data Exposure: Sensitive data exposure through API responses",
        "Security Misconfigurations: API configuration security assessment"
      ]
    }
  ];

  const apiTypes = [
    { name: "REST APIs", description: "RESTful web services and microservices" },
    { name: "GraphQL APIs", description: "GraphQL endpoints and schema validation" },
    { name: "SOAP APIs", description: "SOAP-based web services and WSDL analysis" },
    { name: "WebSocket APIs", description: "Real-time WebSocket communication security" },
    { name: "gRPC APIs", description: "gRPC service security assessment" },
    { name: "Webhook APIs", description: "Webhook endpoint security validation" }
  ];

  const securityTests = [
    {
      category: "Authentication & Authorization",
      tests: [
        "JWT Token Validation: JSON Web Token security assessment",
        "OAuth Flow Testing: OAuth 2.0 and OpenID Connect validation",
        "API Key Security: API key management and security testing",
        "Session Management: API session handling and security"
      ]
    },
    {
      category: "Input Validation & Injection",
      tests: [
        "SQL Injection: Database injection through API parameters",
        "NoSQL Injection: NoSQL database injection testing",
        "Command Injection: OS command injection via API inputs",
        "LDAP Injection: LDAP injection through API parameters"
      ]
    },
    {
      category: "Business Logic & Data",
      tests: [
        "Mass Assignment: Object property injection vulnerabilities",
        "Excessive Data Exposure: Sensitive information leakage",
        "Lack of Resources & Rate Limiting: Resource exhaustion testing",
        "Improper Assets Management: API versioning and lifecycle issues"
      ]
    },
    {
      category: "Configuration & Infrastructure",
      tests: [
        "Security Misconfiguration: API server and framework misconfigurations",
        "Insufficient Logging: API activity logging and monitoring gaps",
        "Improper Error Handling: Information disclosure through error messages",
        "CORS Misconfiguration: Cross-Origin Resource Sharing security issues"
      ]
    }
  ];

  const vulnerabilityCategories = [
    { category: "Critical API Flaws", description: "Severe API vulnerabilities requiring immediate attention", color: "text-red-500" },
    { category: "High-Risk Issues", description: "Significant API security concerns with high impact", color: "text-orange-500" },
    { category: "Medium-Risk Issues", description: "Moderate API security issues requiring remediation", color: "text-yellow-500" },
    { category: "Low-Risk Issues", description: "Minor API security concerns and best practice violations", color: "text-blue-500" }
  ];

  const integrationCapabilities = [
    {
      title: "Development Integration",
      integrations: [
        "OpenAPI/Swagger: Integration with OpenAPI specifications",
        "Postman Collections: Import and test Postman API collections",
        "CI/CD Pipelines: Automated API security testing in pipelines",
        "API Gateways: Integration with Kong, AWS API Gateway, Azure APIM"
      ]
    },
    {
      title: "Testing Frameworks",
      integrations: [
        "Newman Integration: Postman collection runner integration",
        "Insomnia Support: Insomnia workspace and collection import",
        "Custom Scripts: Support for custom API testing scripts",
        "Load Testing: Integration with API load testing tools"
      ]
    },
    {
      title: "Monitoring & Analytics",
      integrations: [
        "API Monitoring: Real-time API security monitoring",
        "SIEM Integration: Security event correlation and alerting",
        "Metrics Collection: API security metrics and KPI tracking",
        "Reporting Tools: Integration with security reporting platforms"
      ]
    }
  ];

  const bestPractices = [
    {
      category: "API Security Implementation",
      practices: [
        "Authentication: Implement strong API authentication mechanisms",
        "Authorization: Enforce proper API authorization and access controls",
        "Input Validation: Validate all API inputs and parameters",
        "Rate Limiting: Implement API rate limiting and throttling",
        "HTTPS Enforcement: Ensure all API communications use HTTPS"
      ]
    },
    {
      category: "Testing & Monitoring",
      practices: [
        "Regular Testing: Implement continuous API security testing",
        "Automated Scanning: Automate API vulnerability scanning",
        "Monitoring: Monitor API usage and security events",
        "Documentation: Maintain up-to-date API security documentation",
        "Incident Response: Establish API security incident response procedures"
      ]
    },
    {
      category: "Development Practices",
      practices: [
        "Secure Design: Design APIs with security as a primary consideration",
        "Least Privilege: Implement principle of least privilege for API access",
        "Error Handling: Implement secure error handling and logging",
        "Versioning: Maintain secure API versioning and lifecycle management",
        "Testing: Include security testing in API development lifecycle"
      ]
    }
  ];

  const advancedFeatures = [
    {
      title: "Intelligent Testing",
      features: [
        "Smart Fuzzing: AI-powered API parameter fuzzing and testing",
        "Behavioral Analysis: API usage pattern analysis and anomaly detection",
        "Schema Validation: Automatic API schema validation and testing",
        "Business Logic Testing: Advanced business logic vulnerability detection"
      ]
    },
    {
      title: "Automation & Orchestration",
      features: [
        "Automated Workflows: Automated API security testing workflows",
        "Custom Rules: Custom security rules and validation logic",
        "Scheduled Scanning: Automated scheduled API security scans",
        "Integration APIs: RESTful APIs for custom integrations"
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="API Security Testing" />
        
        {/* Hero Section */}
        <MotionDiv delay={0.1} variant="fadeIn">
          <div className="text-center mb-12">
            <MotionDiv delay={0.15} variant="bounce">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-primary-500/10 rounded-full">
                  <FiLink className="w-16 h-16 text-primary-500" />
                </div>
              </div>
            </MotionDiv>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              The API Security module in Ana Security provides comprehensive security testing for REST, GraphQL, SOAP, and other API types, identifying vulnerabilities specific to API architectures and modern microservices.
            </p>
            
            <MotionDiv delay={0.2} variant="scale">
              <div className="mb-8">
                <img 
                  src="/screenshots/security-modules/09_api-security.png" 
                  alt="API Security Dashboard"
                  className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full max-w-4xl mx-auto hover:shadow-xl transition-shadow duration-300"
                />
              </div>
            </MotionDiv>
          </div>
        </MotionDiv>

        {/* Current Statistics */}
        <MotionDiv delay={0.3} variant="slideLeft">
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              API Security Dashboard - Current Statistics
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {currentStats.map((stat, index) => (
                <MotionDiv key={index} delay={0.4 + index * 0.1} variant="bounce" hover>
                  <div className="text-center p-6 bg-light-hover dark:bg-dark-hover rounded-lg">
                    <div className="text-3xl text-primary-500 mb-3 flex justify-center">
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {stat.description}
                    </div>
                  </div>
                </MotionDiv>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dashboard Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>API Discovery:</strong> Automatic discovery and mapping of API endpoints</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Security Testing:</strong> Comprehensive API security vulnerability testing</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Real-time Monitoring:</strong> Continuous API security monitoring</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Compliance Reporting:</strong> API security compliance assessment</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Core Features */}
        <MotionDiv delay={0.2}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              API Security Core Features
            </h2>
            
            <div className="space-y-8">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-2xl text-primary-500">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Supported API Types */}
        <MotionDiv delay={0.3}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Supported API Types
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Comprehensive security testing across all major API architectures and protocols:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {apiTypes.map((api, index) => (
                <div key={index} className="p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {api.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {api.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Security Tests */}
        <MotionDiv delay={0.4}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              API Security Tests
            </h2>
            
            <div className="space-y-8">
              {securityTests.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.tests.map((test, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiZap className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{test}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Vulnerability Categories */}
        <MotionDiv delay={0.5}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              API Vulnerability Categories (18 Found)
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {vulnerabilityCategories.map((category, index) => (
                <div key={index} className="p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                  <h3 className={`font-semibold mb-2 ${category.color}`}>
                    {category.category}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {category.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                OWASP API Security Top 10
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Broken Object Level Authorization:</strong> BOLA/IDOR vulnerabilities</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Broken User Authentication:</strong> Authentication bypass vulnerabilities</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Excessive Data Exposure:</strong> Sensitive information leakage</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Lack of Resources & Rate Limiting:</strong> Resource exhaustion attacks</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Integration Capabilities */}
        <MotionDiv delay={0.6}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Integration Capabilities
            </h2>
            
            <div className="space-y-8">
              {integrationCapabilities.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.integrations.map((integration, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiSettings className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{integration}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Advanced Features */}
        <MotionDiv delay={0.7}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Advanced Features
            </h2>
            
            <div className="space-y-8">
              {advancedFeatures.map((feature, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {feature.features.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiShield className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Best Practices */}
        <MotionDiv delay={0.8}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              API Security Best Practices
            </h2>
            
            <div className="space-y-8">
              {bestPractices.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.practices.map((practice, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Call to Action */}
        <MotionDiv delay={0.9}>
          <div className="text-center bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Secure Your APIs?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Start using API Security to identify and fix vulnerabilities in your REST, GraphQL, and other API endpoints.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MotionDiv delay={1.0} variant="scale" hover>
                <a 
                  href="/ana-actions" 
                  className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
                >
                  <FiCheckCircle className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Use ANA Actions</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Automatically fix API vulnerabilities</p>
                </a>
              </MotionDiv>
              
              <MotionDiv delay={1.1} variant="scale" hover>
                <a 
                  href="/dashboard" 
                  className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
                >
                  <FiTrendingUp className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">View Dashboard</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Monitor your API security</p>
                </a>
              </MotionDiv>
              
              <MotionDiv delay={1.2} variant="scale" hover>
                <a 
                  href="/integrations" 
                  className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
                >
                  <FiSettings className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Setup Integrations</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Connect your API testing tools</p>
                </a>
              </MotionDiv>
            </div>
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default APISecurity;
